import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormFieldsValidationModel,
  IIdConditionsAgeLimit,
  IIdConditionsModel,
} from '../../../models/form-fields-formly-validation.model';
import {
  FieldValidationPatternConfigEnumToDescription,
  FieldValidationPatternConfigEnumToRegexString,
} from '../../field-validation-pattern-config-conversion.utils';
import {
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
} from '@irembo-andela/irembogov3-common';
import { ValidationTypeDefaultMessage } from '../form-builder-validation-type-message.utils';
import { AttachmentFieldFileFormat } from '../../../constants/attachment-field-file-format-emum-to-types.enum';

/**
 * The static `props.required` expression the Required checkbox generates: a field
 * is required only while it is visible (not hidden/non-display) AND its Required
 * toggle is on. The visual Expressions editor authors DIFFERENT strings here when
 * a designer adds a conditional "required when …" rule, so this constant is also
 * the sentinel used to tell an authored expression apart from the default.
 */
export const DEFAULT_PROPS_REQUIRED_EXPRESSION =
  '!(field?.props?.hideField || field?.hide) && field?.props?.defaultRequired';

/**
 * True when `props.required` carries a designer-authored conditional expression
 * (from the Expressions tab) rather than the default checkbox-driven one. Used to
 * protect that expression from being clobbered when the Validation/Settings save
 * regenerates the field via the V1 setters.
 */
export const isCustomRequiredExpression = (
  expression: unknown
): expression is string =>
  typeof expression === 'string' &&
  expression.trim().length > 0 &&
  expression !== DEFAULT_PROPS_REQUIRED_EXPRESSION;

/**
 * Run a validation/settings update that may overwrite `props.required`, then
 * restore a designer-authored conditional `props.required` expression if one was
 * present beforehand. The V1 setters assume they exclusively own `props.required`
 * and always rewrite it to the default; in V2 the Expressions tab is a co-owner,
 * so a conditional "required when …" rule must survive an unrelated save.
 */
export const preserveCustomRequiredExpression = (
  field: FormlyFieldConfig,
  updateFn: (field: FormlyFieldConfig) => FormlyFieldConfig
): FormlyFieldConfig => {
  const authored = field?.expressions?.['props.required'];
  const updated = updateFn(field);
  if (isCustomRequiredExpression(authored)) {
    updated.expressions = {
      ...(updated.expressions ?? {}),
      'props.required': authored,
    };
  }
  return updated;
};

export const setRequiredValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.required);

  const requiredStatus: boolean = model.required ?? false;

  if (field.props) {
    field.props.required = requiredStatus;
    field.props['defaultRequired'] = requiredStatus;
  } else {
    field.props = {
      required: requiredStatus,
      defaultRequired: requiredStatus,
    };
  }

  const propsRequiredExpression = DEFAULT_PROPS_REQUIRED_EXPRESSION;

  if (field.expressions) {
    field.expressions = {
      ...field.expressions,
      'props.required': propsRequiredExpression,
    };
  } else {
    field.expressions = {
      'props.required': propsRequiredExpression,
    };
  }

  const requiredMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.required] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.required);

  if (requiredStatus) {
    updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.required,
      requiredMessage
    );
  }

  return field;
};

export const setIdConditionsValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minimumAge);
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maximumAge);

  const minimumAgeLimit: number | undefined =
    model.idConditions?.ageLimit?.minimumAge ?? undefined;
  const maximumAgeLimit: number | undefined =
    model.idConditions?.ageLimit?.maximumAge ?? undefined;
  const dateFieldKey: string =
    model.idConditions?.ageLimit?.key ?? 'dateOfBirth';

  const ageLimitConfig: IIdConditionsAgeLimit = {
    key: dateFieldKey,
    minimumAge: minimumAgeLimit,
    maximumAge: maximumAgeLimit,
  };

  const fieldIdConditions: IIdConditionsModel = {
    ...(field?.props?.['idConditions'] ?? {}),
    ageLimit: ageLimitConfig,
  };

  if (field.props) {
    field.props['idConditions'] = fieldIdConditions;
  } else {
    field.props = {
      idConditions: fieldIdConditions,
    };
  }

  const minimumAgeMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.minimumAge] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minimumAge);

  const maximumAgeMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.maximumAge] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maximumAge);

  if (minimumAgeMessage) {
    updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.minimumAge,
      minimumAgeMessage
    );
  }
  if (maximumAgeMessage) {
    updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.maximumAge,
      maximumAgeMessage
    );
  }

  return field;
};
export const setMinValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.min);
  let min: number | undefined = model.min ?? undefined;
  min = min === 0 ? undefined : min;
  if (field.props) {
    field.props.min = min;
  } else {
    field.props = {
      min,
    };
  }

  const minMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.min] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.min);

  if (minMessage) {
    updateFieldMessages(field, EIremboFormlyValidationTypes.min, minMessage);
  }

  return field;
};

export const setMaxValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.max);
  let max: number | undefined = model.max ?? undefined;
  max = max === 0 ? undefined : max;

  if (field.props) {
    field.props.max = max;
  } else {
    field.props = {
      max,
    };
  }

  const maxMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.max] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.max);

  if (maxMessage) {
    updateFieldMessages(field, EIremboFormlyValidationTypes.max, maxMessage);
  }

  return field;
};

export const setMinLengthValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minLength);
  let minLength: number | undefined = model.minLength ?? undefined;
  minLength = minLength === 0 ? undefined : minLength;
  if (field.props) {
    field.props.minLength = minLength;
  } else {
    field.props = {
      minLength,
    };
  }

  const minLengthMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.minLength] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minLength);

  if (minLength) {
    updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.minLength,
      minLengthMessage
    );
  }

  return field;
};

export const setMaxLengthValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maxLength);
  let maxLength: number | undefined = model.maxLength ?? undefined;
  maxLength = maxLength === 0 ? undefined : maxLength;

  if (field.props) {
    field.props.maxLength = maxLength;
  } else {
    field.props = {
      maxLength,
    };
  }

  const maxLengthMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.maxLength] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maxLength);

  if (maxLength) {
    updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.maxLength,
      maxLengthMessage
    );
  }

  return field;
};

export const setPatternValidatorUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.pattern);

  let regexPattern: string | undefined =
    FieldValidationPatternConfigEnumToRegexString(
      model.patternValidation?.patternType,
      model.patternValidation?.customPattern
    );
  let patternDescription: string | undefined =
    FieldValidationPatternConfigEnumToDescription(
      model.patternValidation?.patternType
    ) ?? `pattern match ${model.patternValidation?.customPattern}`;

  if (!model.patternValidation?.patternType) {
    regexPattern = undefined;
    patternDescription = undefined;
  }

  const patternMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.pattern] ??
    `${ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.pattern
    )} : accepts ${patternDescription}`;

  if (regexPattern) {
    updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.pattern,
      patternMessage
    );
  }

  if (field.props) {
    field.props.pattern = regexPattern;
  } else {
    field.props = {
      pattern: regexPattern,
    };
  }
  return field;
};

export const setUniqueFieldValidatorFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.uniqueField);

  if (field?.validators?.validation) {
    const filteredValidators: Record<string, unknown>[] =
      field.validators.validation.filter(
        (v: any) => v?.name !== EIremboFormlyValidationTypes.uniqueField
      );
    field.validators.validation = filteredValidators;
  } else if (field.validators) {
    field.validators.validation = [];
  } else {
    field.validators = { validation: [] };
  }

  if (!model.uniqueField || model.uniqueField.length < 1) return field;

  field.validators.validation.push({
    name: EIremboFormlyValidationTypes.uniqueField,
    options: { fieldKeys: model.uniqueField },
  });

  const uniqueFieldMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.uniqueField] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.uniqueField);

  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.uniqueField,
    uniqueFieldMessage
  );

  return field;
};

export const setPopulatesFieldValidatorFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.invalidId);
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.invalidInput);

  const invalidIdMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidId] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidId);

  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.invalidId,
    invalidIdMessage
  );

  const invalidInputMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidInput] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidInput);
  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.invalidInput,
    invalidInputMessage
  );

  return field;
};

export const setFileSizeValidatorFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.minimumUploadSize);
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.maximumUploadSize);

  const minimumUploadSize: number | undefined =
    model.fileSize?.minimumUploadSize ?? undefined;
  const maximumUploadSize: number | undefined =
    model.fileSize?.maximumUploadSize ?? undefined;

  if (field.props) {
    field.props['minimumUploadSize'] = minimumUploadSize;
  } else {
    field.props = {
      minimumUploadSize,
    };
  }

  if (field.props) {
    field.props['maximumUploadSize'] = maximumUploadSize;
  } else {
    field.props = {
      maximumUploadSize,
    };
  }

  if (minimumUploadSize) {
    const minimumUploadSizeMessage: string =
      model.validationMessages?.[
        EIremboFormlyValidationTypes.minimumUploadSize
      ] ??
      ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.minimumUploadSize
      );

    field = updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.minimumUploadSize,
      minimumUploadSizeMessage
    );
  }

  if (maximumUploadSize) {
    const maximumUploadSizeMessage: string =
      model.validationMessages?.[
        EIremboFormlyValidationTypes.maximumUploadSize
      ] ??
      ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.maximumUploadSize
      );

    field = updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.maximumUploadSize,
      maximumUploadSizeMessage
    );
  }

  return field;
};

export const setAllowedFileFormatsValidatorFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.invalidfileformat);

  let allowedFormats: string[] | undefined = [];

  Object.keys(AttachmentFieldFileFormat).forEach(key => {
    if (model?.fileFormats?.[key]) {
      allowedFormats = [
        ...AttachmentFieldFileFormat[key].value,
        ...(allowedFormats ?? []),
      ];
    }
  });

  if (field.props) {
    field.props['allowedFormats'] = allowedFormats;
  } else {
    field.props = {
      allowedFormats,
    };
  }

  if (allowedFormats.length > 0) {
    const allowedFormatsMessage: string =
      model.validationMessages?.[
        EIremboFormlyValidationTypes.invalidfileformat
      ] ??
      ValidationTypeDefaultMessage(
        EIremboFormlyValidationTypes.invalidfileformat
      );

    field = updateFieldMessages(
      field,
      EIremboFormlyValidationTypes.invalidfileformat,
      allowedFormatsMessage
    );
  }

  return field;
};
export const setInvalidIntlPhoneNumberFormatUpdateFieldFromModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(
    field,
    EIremboFormlyValidationTypes.invalidNumberFormat
  );

  const invalidNumberFormatMessage: string =
    model.validationMessages?.[
      EIremboFormlyValidationTypes.invalidNumberFormat
    ] ??
    ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidNumberFormat
    );

  const invalidNumberMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidNumber] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidNumber);

  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.invalidNumberFormat,
    invalidNumberFormatMessage
  );

  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.invalidNumber,
    invalidNumberMessage
  );
  return field;
};

export const updateFieldMessages = (
  field: FormlyFieldConfig,
  messageKey: EIremboFormlyValidationTypes,
  message: string | undefined
): FormlyFieldConfig => {
  if (!message) {
    return clearFieldMessageByKey(field, messageKey);
  }

  if (field.validation?.messages) {
    field.validation.messages[messageKey] = message;
    return field;
  }

  if (!field.validation?.messages) {
    const messages: Record<string, string> = {};
    messages[messageKey] = message;
    field.validation = { messages };
    return field;
  }

  return field;
};

export const clearFieldMessageByKey = (
  field: FormlyFieldConfig,
  messageKey: EIremboFormlyValidationTypes
): FormlyFieldConfig => {
  if (field.validation?.messages) {
    delete field.validation.messages[messageKey];
  }
  return field;
};

export const setInvalidTinFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.invalidTIN);
  clearFieldMessageByKey(field, EIremboFormlyValidationTypes.invalidInput);

  const invalTinIdMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidTIN] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidTIN);

  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.invalidTIN,
    invalTinIdMessage
  );

  const invalidInputMessage: string =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidInput] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidInput);
  updateFieldMessages(
    field,
    EIremboFormlyValidationTypes.invalidInput,
    invalidInputMessage
  );

  return field;
};

export const setInvalidGenericInputFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages[EIremboFormlyValidationTypes.invalidGenericInput] =
    model.validationMessages?.[
      EIremboFormlyValidationTypes.invalidGenericInput
    ] ??
    ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidGenericInput
    );

  field.validation.messages[EIremboFormlyValidationTypes.invalidInput] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidInput] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidInput);

  return field;
};

export const setInvalidAppNoFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages[EIremboFormlyValidationTypes.invalidAppNo] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidAppNo] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidAppNo);

  field.validation.messages[EIremboFormlyValidationTypes.invalidInput] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidInput] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidInput);

  return field;
};

export const setInvalidInputFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages[EIremboFormlyValidationTypes.invalidInput] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidInput] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidInput);

  return field;
};

export const setMaxAndMinSelectionFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages[EIremboFormlyValidationTypes.minSelection] =
    model.validationMessages?.[EIremboFormlyValidationTypes.minSelection] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minSelection);

  field.validation.messages[EIremboFormlyValidationTypes.maxSelection] =
    model.validationMessages?.[EIremboFormlyValidationTypes.maxSelection] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maxSelection);
  return field;
};

export const setInvalidIdFormDataIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages[EIremboFormlyValidationTypes.invalidId] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidId] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidId);

  field.validation.messages[EIremboFormlyValidationTypes.invalidInput] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidInput] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidInput);

  field.validation.messages[EIremboFormlyValidationTypes.pattern] =
    model.validationMessages?.[EIremboFormlyValidationTypes.pattern] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.pattern);

  return field;
};

export const setEmailValidatorMessageIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages[EIremboFormlyValidationTypes.email] =
    model.validationMessages?.[EIremboFormlyValidationTypes.email] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.email);

  return field;
};

export const setDateTimePickerValidatorMessageIntoField = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!field.validation) {
    field.validation = { messages: {} };
  }
  if (!field.validation.messages) {
    field.validation.messages = {};
  }

  field.validation.messages['invalid'] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidDate] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidDate);

  field.validation.messages[EIremboFormlyValidationTypes.minDate] =
    model.validationMessages?.[EIremboFormlyValidationTypes.minDate] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.minDate);

  field.validation.messages[EIremboFormlyValidationTypes.maxDate] =
    model.validationMessages?.[EIremboFormlyValidationTypes.maxDate] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.maxDate);

  field.validation.messages[EIremboFormlyValidationTypes.unavailableSlots] =
    model.validationMessages?.[EIremboFormlyValidationTypes.unavailableSlots] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.unavailableSlots);

  field.validation.messages[
    EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
  ] =
    model.validationMessages?.[
      EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
    ] ??
    ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
    );

  field.validation.messages[
    EIremboFormlyValidationTypes.dateAndTimeValuesRequired
  ] =
    model.validationMessages?.[
      EIremboFormlyValidationTypes.dateAndTimeValuesRequired
    ] ??
    ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.dateAndTimeValuesRequired
    );

  field.validation.messages[EIremboFormlyValidationTypes.invalidTimeValue] =
    model.validationMessages?.[EIremboFormlyValidationTypes.invalidTimeValue] ??
    ValidationTypeDefaultMessage(EIremboFormlyValidationTypes.invalidTimeValue);

  field.validation.messages[
    EIremboFormlyValidationTypes.invalidDateTimeFormat
  ] =
    model.validationMessages?.[
      EIremboFormlyValidationTypes.invalidDateTimeFormat
    ] ??
    ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidDateTimeFormat
    );

  return field;
};
